import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { Plus, Trash } from "phosphor-react";
import fieldTemplatesStyles from "../Settings/FieldTemplatesSettings/FieldTemplatesStyles";
import { fget } from "../../API/callsAPI";

const detectFieldType = (fieldName) => {
  const lowerName = fieldName.toLowerCase();

  if (lowerName === "created_at" || lowerName === "updated_at") {
    return "date";
  }

  if (
    lowerName === "created_by" ||
    lowerName === "assigned_user" ||
    lowerName === "updated_by"
  ) {
    return "user";
  }

  return "text";
};

const getOperatorsByType = (fieldType) => {
  switch (fieldType) {
    case "date":
      return [
        { value: "equals", label: "is" },
        { value: "not_equals", label: "is not" },
        { value: "less_than", label: "is before" },
        { value: "greater_than", label: "is after" },
        { value: "is_empty", label: "is empty" },
        { value: "is_not_empty", label: "is not empty" },
      ];
    case "user":
      return [
        { value: "equals", label: "is" },
        { value: "not_equals", label: "is not" },
        { value: "is_empty", label: "is empty" },
        { value: "is_not_empty", label: "is not empty" },
      ];
    case "text":
    default:
      return [
        { value: "equals", label: "is" },
        { value: "not_equals", label: "is not" },
        { value: "contains", label: "contains" },
        { value: "not_contains", label: "does not contain" },
        { value: "is_empty", label: "is empty" },
        { value: "is_not_empty", label: "is not empty" },
      ];
  }
};

const FilterRow = ({ filter, onUpdate, onDelete, availableFields }) => {
  const classes = fieldTemplatesStyles();

  const selectedField = availableFields.find((f) => f.name === filter.field);
  const fieldType = selectedField
    ? detectFieldType(selectedField.name)
    : "text";
  const operators = getOperatorsByType(fieldType);

  const needsValueInput =
    filter.operator && !["is_empty", "is_not_empty"].includes(filter.operator);

  const handleFieldChange = (newField) => {
    onUpdate({ ...filter, field: newField, operator: "", value: "" });
  };

  const handleOperatorChange = (newOperator) => {
    onUpdate({ ...filter, operator: newOperator, value: "" });
  };

  const handleValueChange = (newValue) => {
    onUpdate({ ...filter, value: newValue });
  };

  const renderValueInput = () => {
    if (!needsValueInput) return null;

    switch (fieldType) {
      case "date":
        return (
          <Box className={classes.cardContentDiv3} sx={{ flex: 1 }}>
            <TextField
              type="date"
              value={filter.value || ""}
              onChange={(e) => handleValueChange(e.target.value)}
              fullWidth
              sx={{
                "& .MuiInputBase-input": {
                  padding: "8px",
                  backgroundColor: "transparent",
                  color: "white !important",
                  fontSize: "14px",
                  fontFamily: "poppins",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "transparent" },
                  "&.Mui-focused fieldset": { borderColor: "transparent" },
                },
              }}
            />
          </Box>
        );

      case "user":
        return (
          <Box className={classes.cardContentDiv3} sx={{ flex: 1 }}>
            <Autocomplete
              value={filter.value || null}
              onChange={(e, newValue) => handleValueChange(newValue)}
              options={[]}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Select ${selectedField?.name || "user"}`}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8px !important",
                      backgroundColor: "transparent",
                      color: "white !important",
                      fontSize: "14px",
                      fontFamily: "poppins",
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "transparent" },
                      "&.Mui-focused fieldset": { borderColor: "transparent" },
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiAutocomplete-popupIndicator": { color: "white" },
                "& .MuiAutocomplete-clearIndicator": { color: "white" },
              }}
              ListboxProps={{
                sx: {
                  backgroundColor: "#252A38",
                  color: "white",
                  "& .MuiAutocomplete-option": {
                    "&:hover": { backgroundColor: "#2D3240" },
                    '&[aria-selected="true"]': {
                      backgroundColor: "#6C65D9",
                      "&:hover": { backgroundColor: "#5A55C7" },
                    },
                  },
                },
              }}
            />
          </Box>
        );

      case "text":
      default:
        return (
          <Box className={classes.cardContentDiv3} sx={{ flex: 1 }}>
            <TextField
              value={filter.value || ""}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder="Enter value"
              fullWidth
              sx={{
                "& .MuiInputBase-input": {
                  padding: "8px",
                  backgroundColor: "transparent",
                  color: "white !important",
                  fontSize: "14px",
                  fontFamily: "poppins",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "transparent" },
                  "&.Mui-focused fieldset": { borderColor: "transparent" },
                },
              }}
            />
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        mb: 2,
      }}
    >
      <Box className={classes.cardContentDiv3} sx={{ flex: 1 }}>
        <Select
          value={filter.field || ""}
          onChange={(e) => handleFieldChange(e.target.value)}
          fullWidth
          displayEmpty
          sx={{
            color: "white",
            fontSize: "14px",
            fontFamily: "poppins",
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiSelect-select": { padding: "8px" },
            "& .MuiSvgIcon-root": { color: "white" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#252A38",
                color: "white",
                "& .MuiMenuItem-root": {
                  "&:hover": { backgroundColor: "#2D3240" },
                  "&.Mui-selected": {
                    backgroundColor: "#6C65D9",
                    "&:hover": { backgroundColor: "#5A55C7" },
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="" disabled>
            Filter Type
          </MenuItem>
          {availableFields.map((f) => (
            <MenuItem key={f.name} value={f.name}>
              {f.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box className={classes.cardContentDiv3} sx={{ flex: 1 }}>
        <Select
          value={filter.operator || ""}
          onChange={(e) => handleOperatorChange(e.target.value)}
          fullWidth
          displayEmpty
          disabled={!filter.field}
          sx={{
            color: "white",
            fontSize: "14px",
            fontFamily: "poppins",
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiSelect-select": { padding: "8px" },
            "& .MuiSvgIcon-root": { color: "white" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#252A38",
                color: "white",
                "& .MuiMenuItem-root": {
                  "&:hover": { backgroundColor: "#2D3240" },
                  "&.Mui-selected": {
                    backgroundColor: "#6C65D9",
                    "&:hover": { backgroundColor: "#5A55C7" },
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="" disabled>
            Operator
          </MenuItem>
          {operators.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {renderValueInput()}

      <IconButton
        onClick={onDelete}
        sx={{
          color: "#FF6B6B",
          padding: "8px",
          "&:hover": {
            backgroundColor: "rgba(255, 107, 107, 0.1)",
          },
        }}
      >
        <Trash size={20} />
      </IconButton>
    </Box>
  );
};

export default function CustomPageConditionsEdit({
  projectId,
  trackType,
  trackableId,
  initialFilter,
  onFilterChange,
}) {
  const classes = fieldTemplatesStyles();
  const [viewMode, setViewMode] = useState("all");
  const [specificTrackables, setSpecificTrackables] = useState([]);
  const [filters, setFilters] = useState(initialFilter || []);
  const [availableFields, setAvailableFields] = useState([]);

  useEffect(() => {
    if (trackableId) {
      handleFetchAvailableFields(trackableId);
    }
  }, [trackableId]);

  useEffect(() => {
    if (onFilterChange) {
      const operatorMap = {
        equals: "=",
        not_equals: "!=",
        contains: "contains",
        not_contains: "not_contains",
        less_than: "<",
        greater_than: ">",
        is_empty: "is_empty",
        is_not_empty: "is_not_empty",
      };

      const formattedFilters = filters
        .filter(
          (f) =>
            f.field &&
            f.operator &&
            (f.value || ["is_empty", "is_not_empty"].includes(f.operator))
        )
        .map((f) => [
          f.field,
          operatorMap[f.operator] || f.operator,
          f.value || "",
        ]);

      onFilterChange(formattedFilters);
    }
  }, [filters, trackType]);

  const handleFetchAvailableFields = async (trackableId) => {
    try {
      const res = await fget({
        url: `trackables/trackable-field/?project=${projectId}&trackable=${trackableId}`,
      });

      const defaultFields = res?.data?.default_fields.map((field) => ({
        name: field.name,
        isDefault: true,
      }));

      const customFields = res.data?.custom_fields.map((field) => ({
        name: field.name,
        identifier: field.identifier,
        isDefault: false,
      }));

      setAvailableFields([...defaultFields, ...customFields]);
      console.log("Setting available Fields to", availableFields);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddFilter = () => {
    const newFilter = {
      id: Date.now(),
      field: "",
      operator: "",
      value: "",
    };
    setFilters([...filters, newFilter]);
  };

  const handleUpdateFilter = (filterId, updatedFilter) => {
    setFilters(filters.map((f) => (f.id === filterId ? updatedFilter : f)));
  };

  const handleDeleteFilter = (filterId) => {
    setFilters(filters.filter((f) => f.id !== filterId));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box>
        <Typography sx={{ mb: 2, fontSize: "14px" }}>
          Which trackables are viewed?
        </Typography>
        <RadioGroup
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value)}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: "12px",
            }}
          >
            <FormControlLabel
              value="all"
              control={
                <Radio
                  sx={{
                    color: "#606479",
                    "&.Mui-checked": { color: "#6C65D9" },
                  }}
                />
              }
              label={
                <Box>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, color: "white" }}
                  >
                    All trackables
                  </Typography>
                  <Typography
                    sx={{ fontSize: "12px", color: "rgba(255,255,255,0.6)" }}
                  >
                    Display all trackables that would satisfy the below
                    conditional data filters
                  </Typography>
                </Box>
              }
              sx={{
                backgroundColor: "#252A38",
                borderRadius: "10px",
                border: `2px solid ${
                  viewMode === "all" ? "#6C65D9" : "transparent"
                }`,
                p: "12px",
                m: 0,
                transition: "all 0.2s",
              }}
            />
            <FormControlLabel
              value="specific"
              control={
                <Radio
                  sx={{
                    color: "#606479",
                    "&.Mui-checked": { color: "#6C65D9" },
                  }}
                />
              }
              label={
                <Box sx={{ width: "100%" }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "white",
                      mb: 1,
                    }}
                  >
                    Specific trackables
                  </Typography>
                  {viewMode === "specific" && (
                    <Box className={classes.cardContentDiv3}>
                      <Autocomplete
                        multiple
                        value={specificTrackables}
                        onChange={(e, newValue) =>
                          setSpecificTrackables(newValue)
                        }
                        options={[]}
                        freeSolo
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search & choose trackables"
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: "6px !important",
                                backgroundColor: "transparent",
                                color: "white !important",
                                fontSize: "14px",
                                fontFamily: "poppins",
                              },
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "4px",
                                "& fieldset": { borderColor: "transparent" },
                                "&:hover fieldset": {
                                  borderColor: "transparent",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "transparent",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}
                </Box>
              }
              sx={{
                backgroundColor: "#252A38",
                borderRadius: "10px",
                border: `2px solid ${
                  viewMode === "specific" ? "#6C65D9" : "transparent"
                }`,
                p: "12px",
                m: 0,
                transition: "all 0.2s",
                alignItems: "flex-start",
              }}
            />
          </Box>
        </RadioGroup>
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>
            Conditional Data Filters (Ordered from top to bottom)
          </Typography>
          <Button
            onClick={handleAddFilter}
            startIcon={<Plus size={18} />}
            variant="outlined"
            sx={{
              borderColor: "#6C65D9",
              color: "#6C65D9",
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#5A55C7",
                backgroundColor: "rgba(108, 101, 217, 0.1)",
              },
            }}
          >
            Add a filter
          </Button>
        </Box>

        {filters.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr auto",
                gap: "12px",
                mb: 1,
                px: 1,
              }}
            >
              <Typography
                sx={{ fontSize: "12px", color: "#606479", fontWeight: 500 }}
              >
                Filter Type
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "#606479", fontWeight: 500 }}
              >
                Operator
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "#606479", fontWeight: 500 }}
              >
                Value
              </Typography>
              <Box sx={{ width: "36px" }} />
            </Box>
          </Box>
        )}

        {filters.map((filter) => (
          <FilterRow
            key={filter.id}
            filter={filter}
            onUpdate={(updated) => handleUpdateFilter(filter.id, updated)}
            onDelete={() => handleDeleteFilter(filter.id)}
            availableFields={availableFields}
          />
        ))}

        {filters.length === 0 && (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#606479",
              textAlign: "center",
              py: 4,
            }}
          >
            No filters added yet. Click "Add a filter" to get started.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
